import { GetAllAnnouncementsDocument, useCreateAnnouncementMutation } from '@exam-center/graphql';
import {
  type DialogProps,
  Dialog,
  DialogTitle,
  Divider,
  DialogContent,
  DialogActions,
  Button,
  Form,
  FormButton,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { FormProvider, useForm } from 'react-hook-form';
import { z } from 'zod';

import { CreateAnnouncementFormField } from '../../components';

interface CreateAnnouncementModalProps extends DialogProps {
  onClose: () => void;
}
const TITLE_LENGTH_MIN = 1;
const TITLE_LENGTH_MAX = 256;

const CreateAnnouncementFormSchema = z.object({
  title: z
    .string({ message: 'กรุณาระบุหัวข้อข่าว/ประกาศ' })
    .min(TITLE_LENGTH_MIN, 'กรุณาระบุหัวข้อข่าว/ประกาศ')
    .max(TITLE_LENGTH_MAX, `ความยาวต้องไม่เกิน 256 ตัวอักษร`),
  referenceLink: z
    .string()
    .refine(
      (val) => {
        const cleanInputValue = val.trim();
        if (!cleanInputValue) return true;
        return cleanInputValue.startsWith('http://') || cleanInputValue.startsWith('https://');
      },
      {
        message: 'รูปแบบ URL ไม่ถูกต้อง กรุณากรอกลิงก์ที่ขึ้นต้นด้วย http:// หรือ https://',
      },
    )
    .optional(),
});

export type CreateAnnouncementFormValues = z.infer<typeof CreateAnnouncementFormSchema>;
export default function CreateAnnouncementModal(props: Readonly<CreateAnnouncementModalProps>) {
  const { open, onClose } = props;
  const formContext = useForm<CreateAnnouncementFormValues>({
    defaultValues: {
      title: '',
      referenceLink: '',
    },
    resolver: zodResolver(CreateAnnouncementFormSchema),
  });
  const { showSnackbar } = useSnackbar();

  const handleClose = () => {
    onClose();
    formContext.reset();
  };

  const [createAnnouncement] = useCreateAnnouncementMutation({
    onCompleted: () => {
      showSnackbar({ message: 'สร้างประกาศเรียบร้อยแล้ว', variant: 'success' });
      handleClose();
    },
    onError: () => {
      showSnackbar({ message: `สร้างประกาศไม่สำเร็จ`, variant: 'error' });
    },
    awaitRefetchQueries: true,
    refetchQueries: [GetAllAnnouncementsDocument],
  });

  const onSubmit = async (data: CreateAnnouncementFormValues) => {
    await createAnnouncement({
      variables: {
        createAnnouncementInput: {
          title: data.title,
          link: data.referenceLink,
        },
      },
    });
  };
  return (
    <Dialog open={open}>
      <DialogTitle onClose={handleClose}>สร้างประกาศข่าวสาร</DialogTitle>
      <Divider />
      <FormProvider {...formContext}>
        <Form onSubmit={formContext.handleSubmit(onSubmit)}>
          <DialogContent>
            <CreateAnnouncementFormField />
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button onClick={handleClose} color='secondary' variant='outlined' size='large'>
              ยกเลิก
            </Button>
            <FormButton label='สร้างประกาศ' size='large' />
          </DialogActions>
        </Form>
      </FormProvider>
    </Dialog>
  );
}
